import { useAppForm } from '@massilia/features'
import { useResetPasswordMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'

import { AuthErrorComponent } from '../../components/login/auth-error-component'
import { resolveAuthErrorMessage } from '../../lib/auth-error-message'
import { resetPasswordSchema } from '../../lib/schemas'

const searchSchema = z.object({
  token: z.string(),
})

export const Route = createFileRoute('/_auth/reinitialiser-mot-de-passe')({
  component: ResetPasswordComponent,
  validateSearch: searchSchema,
})

function ResetPasswordComponent() {
  const { token } = Route.useSearch()

  const navigate = useNavigate()
  const [submitError, setSubmitError] = useState<null | string>(null)
  const { mutateAsync: resetPassword } = useResetPasswordMutation()

  const form = useAppForm({
    defaultValues: {
      password: '',
      passwordConfirmation: '',
    },
    listeners: {
      onChange: () => {
        if (submitError) {
          setSubmitError(null)
        }
      },
    },
    onSubmit: async ({ value }) => {
      try {
        await resetPassword({
          password: value.password,
          resetPasswordToken: token,
        })
        void navigate({ to: '/connexion' })
      } catch (error) {
        setSubmitError(
          resolveAuthErrorMessage(error, {
            forbiddenMessage: 'Le lien de réinitialisation de mot de passe est invalide.',
          })
        )
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, resetPasswordSchema),
    },
  })

  return (
    <>
      <h1 className="text-gray-light-900 text-center text-xl font-semibold">
        Nouveau mot de passe
      </h1>
      <form
        className="flex w-full flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
      >
        <form.AppField name="password">
          {(field) => (
            <field.TextField
              label="Nouveau mot de passe"
              placeholder="Nouveau mot de passe"
              type="password"
            />
          )}
        </form.AppField>
        <form.AppField name="passwordConfirmation">
          {(field) => (
            <field.TextField
              label="Confirmer le mot de passe"
              placeholder="Confirmer le mot de passe"
              type="password"
            />
          )}
        </form.AppField>
        {submitError && <AuthErrorComponent message={submitError} />}
        <form.Subscribe selector={(state) => state.isSubmitted && !state.isValid}>
          {(hasValidationErrors) =>
            hasValidationErrors ? (
              <AuthErrorComponent message="Veuillez corriger les erreurs du formulaire." />
            ) : null
          }
        </form.Subscribe>
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              className="bg-brand-600 hover:bg-brand-800 w-full px-8 py-2 text-white"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? 'Réinitialisation en cours...' : 'Réinitialiser le mot de passe'}
            </Button>
          )}
        </form.Subscribe>
      </form>
      <Link to="/connexion">
        <Button className="text-brand-700 w-full hover:underline" variant="none">
          Retour
        </Button>
      </Link>
    </>
  )
}
